import type { BusinessJourney, BusinessJourneyStep, EvidenceStatus, TestEvidence } from "./types";

const priority: EvidenceStatus[] = ["failed", "flaky", "stale", "missing", "fixme", "skipped", "passed"];

export function combinedStatus(statuses: EvidenceStatus[]): EvidenceStatus {
  if (statuses.length === 0) return "missing";
  return priority.find((status) => statuses.includes(status)) ?? "missing";
}

/**
 * Resolve one checkpoint against a single test.
 *
 * A journey checkpoint is finer-grained than a Playwright test: four
 * checkpoints commonly share one test. Without step evidence they all inherit
 * that test's verdict, so a failure in the last checkpoint reports the three
 * that demonstrably succeeded as failed too. When the run published the named
 * `test.step` backing this checkpoint, prefer it.
 *
 * The parent status still wins whenever it is *not* a plain pass/fail —
 * skipped, fixme, and stale describe the evidence itself, and a step recorded
 * against a superseded build is no fresher than the test that produced it.
 */
function stepStatusFromTest(
  step: BusinessJourneyStep,
  test: TestEvidence | undefined,
): EvidenceStatus {
  if (!test) return "missing";
  if (!step.evidenceStepTitle) return test.status;
  if (test.status !== "passed" && test.status !== "failed") return test.status;

  const steps = test.steps ?? [];
  // No step evidence at all — an older run, or a spec with no named steps.
  // Nothing finer to say than the parent's verdict.
  if (steps.length === 0) return test.status;

  const match = steps.find((entry) => entry.title === step.evidenceStepTitle);
  // Playwright aborts a test at its first failure, so checkpoints after the
  // failing one are never reported. They did not fail — they never ran, and
  // saying otherwise invents failures QA would go hunting for. A title that
  // drifted lands here too, which CI blocks separately by checking every
  // evidenceStepTitle against the spec.
  if (!match) return "missing";
  return match.status === "missing" ? "missing" : match.status;
}

export function journeyStepStatus(
  step: BusinessJourneyStep,
  testsById: Map<string, TestEvidence>,
): EvidenceStatus {
  if (step.evidenceTestIds.length === 0) return "missing";
  return combinedStatus(
    step.evidenceTestIds.map((id) => stepStatusFromTest(step, testsById.get(id))),
  );
}

export function journeyStatus(
  journey: BusinessJourney,
  testsById: Map<string, TestEvidence>,
): EvidenceStatus {
  return combinedStatus(journey.steps.map((step) => journeyStepStatus(step, testsById)));
}

export interface JourneySummary {
  total: number;
  passed: number;
  missing: number;
  attention: number;
}

export function journeySummary(
  journey: BusinessJourney,
  testsById: Map<string, TestEvidence>,
): JourneySummary {
  const statuses = journey.steps.map((step) => journeyStepStatus(step, testsById));
  return {
    total: statuses.length,
    passed: statuses.filter((status) => status === "passed").length,
    missing: statuses.filter((status) => ["missing", "fixme", "skipped"].includes(status)).length,
    attention: statuses.filter((status) => ["failed", "flaky", "stale"].includes(status)).length,
  };
}
